// components/strategy/StrategyProjectsPanel.jsx — Phase 3B project
// list/create/open/share/transfer. On "Ouvrir", loads the project's
// CURRENT version objects into the live drawing session (session.
// loadFeatureCollection) and remembers {projectId, versionSeq} so
// "Enregistrer" can save back with the correct optimistic-concurrency
// sequence number.
import React, { useEffect, useState } from "react";
import { strategyEndpoints } from "../../lib/strategyApi";
import { FolderOpen, Plus, Share2, Save } from "lucide-react";

export const StrategyProjectsPanel = ({ session, activeProject, setActiveProject }) => {
  const [projects, setProjects] = useState([]);
  const [newName, setNewName] = useState("");
  const [shareTarget, setShareTarget] = useState("");
  const [shareRole, setShareRole] = useState("reader");
  const [status, setStatus] = useState("");

  const refresh = async () => {
    const { data } = await strategyEndpoints.listProjects();
    setProjects(data.projects);
  };
  useEffect(() => { refresh(); }, []);

  const createProject = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const { data } = await strategyEndpoints.createProject(newName.trim(), "");
    setNewName("");
    await refresh();
    await openProject(data.project.id, data.project.version_seq, data.default_layer_id);
  };

  const openProject = async (projectId, versionSeq, knownLayerId) => {
    const { data } = await strategyEndpoints.exportProject(projectId);
    let layerId = knownLayerId;
    if (!layerId) {
      const { data: layersData } = await strategyEndpoints.listLayers(projectId);
      layerId = layersData.layers[0]?.id;
    }
    session.loadFeatureCollection(data.features.map((f) => ({
      id: f.id, type: "Feature", geometry: f.geometry,
      properties: { ...f.properties, object_type: f.properties.object_type },
    })));
    setActiveProject({ id: projectId, versionSeq, layerId });
    setStatus(`Projet chargé (${data.features.length} objet(s))`);
  };

  const saveCurrentVersion = async () => {
    if (!activeProject) return;
    const objects = session.featureCollection.features.map((f) => ({
      id: f.id, layer_id: activeProject.layerId, object_type: f.properties?.object_type,
      geometry: f.geometry, properties: f.properties || {},
    }));
    try {
      const { data } = await strategyEndpoints.saveVersion(activeProject.id, activeProject.versionSeq, objects);
      setActiveProject({ ...activeProject, versionSeq: activeProject.versionSeq + 1 });
      setStatus(`Enregistré — version ${data.version.version_number}`);
      refresh();
    } catch (e) {
      if (e?.response?.status === 409) setStatus("Conflit — une autre sauvegarde a eu lieu, rechargez le projet.");
      else setStatus(e?.response?.data?.detail?.reason_code || "Échec de l'enregistrement");
    }
  };

  const share = async (projectId) => {
    if (!shareTarget.trim()) return;
    await strategyEndpoints.shareProject(projectId, shareTarget.trim(), shareRole);
    setShareTarget("");
    refresh();
  };

  return (
    <div className="space-y-3" data-testid="strategy-projects-panel">
      <form onSubmit={createProject} className="flex gap-2">
        <input data-testid="strategy-new-project-name" value={newName} onChange={(e) => setNewName(e.target.value)}
          placeholder="Nom du nouveau projet" className="flex-1 border-2 border-rescue-line px-3 py-2 text-sm" />
        <button type="submit" data-testid="strategy-create-project-btn" className="bg-rescue-ink text-white px-3 py-2 text-xs font-bold uppercase flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Créer
        </button>
      </form>

      {activeProject && (
        <button onClick={saveCurrentVersion} data-testid="strategy-save-version-btn"
          className="w-full bg-rescue-red text-white py-2 text-xs font-bold uppercase flex items-center justify-center gap-2">
          <Save className="w-3.5 h-3.5" /> Enregistrer la version
        </button>
      )}
      {status && <div className="text-xs text-rescue-mute" data-testid="strategy-projects-status">{status}</div>}

      <div className="space-y-2" data-testid="strategy-projects-list">
        {projects.map((p) => (
          <div key={p.id} className="border-2 border-rescue-line p-2 text-xs" data-testid={`strategy-project-row-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-bold">{p.name} <span className="text-rescue-mute font-normal">({p.my_role})</span></div>
              <button onClick={() => openProject(p.id, p.version_seq)} data-testid={`strategy-open-project-${p.id}`}
                className="flex items-center gap-1 text-rescue-ink font-bold uppercase text-[10px]">
                <FolderOpen className="w-3.5 h-3.5" /> Ouvrir
              </button>
            </div>
            {p.my_role === "owner" && (
              <div className="flex gap-1 mt-2">
                <input data-testid={`strategy-share-target-${p.id}`} value={shareTarget} onChange={(e) => setShareTarget(e.target.value)}
                  placeholder="ID du compte à partager" className="flex-1 border border-rescue-line px-2 py-1 text-[11px]" />
                <select data-testid={`strategy-share-role-${p.id}`} value={shareRole} onChange={(e) => setShareRole(e.target.value)} className="border border-rescue-line text-[11px]">
                  <option value="reader">Lecteur</option>
                  <option value="editor">Éditeur</option>
                </select>
                <button onClick={() => share(p.id)} data-testid={`strategy-share-btn-${p.id}`} className="text-rescue-ink"><Share2 className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        ))}
        {!projects.length && <div className="text-xs text-rescue-mute" data-testid="strategy-no-projects">Aucun projet pour le moment.</div>}
      </div>
    </div>
  );
};
